import React, { useState } from "react";
import { TableRow, TableCell, IconButton } from "@mui/material";
import { Info, Delete, Edit } from "@mui/icons-material";
import { GroupedOffer } from "./types";
import OfferInfoModal from "./modals/OfferInfoModal";
import OfferEditModal from "./modals/OfferEditModal";
import OfferDeleteModal from "./modals/OfferDeleteModal";
import ItemDetailLightModal from "../../skin-db/offer-details/ItemDetailLightModal";
import { RARITY_COLORS, RarityGrade } from '../../utils/RarityColors';

const statusLabels: Record<string, string> = {
  ACTIVE: "Aktiv",
  INACTIVE: "Inaktiv",
  REMOVED: "Entfernt",
  SOLD_PAYMENT_PENDING: "Warte auf Zahlung",
  SOLD_SELLER_SEND_ITEM_PENDING: "Warte auf Versand",
  SOLD_BUYER_ACCEPT_ITEM_PENDING: "Warte auf Annahme",
  SOLD_7DAY_PENDING: "Steam 7-Tage Sperre",
  BUYER_REVERSED: "Käufer abgebrochen",
  SELLER_REVERSED: "Verkäufer abgebrochen",
  SOLD_SUCCESSFUL: "Erfolgreich verkauft",
};

type Props = {
  offer: GroupedOffer;
  onOffersDeleted: (ids: string[]) => void;
  onOffersUpdated: (updated: { offerId: string; price: number }[]) => void;
};

const cellPaddingSX = { p: { xs: 0, md: 0, lg: 2 } };

const OnSaleRow: React.FC<Props> = ({
                                      offer,
                                      onOffersDeleted,
                                      onOffersUpdated,
                                    }) => {
  const quantity = offer.offers?.length ?? 0;
  const floatValue = offer.item?.details?.floatValue ?? null;
  const price = typeof offer.price === "number" ? offer.price : null;

  const rawStatus = offer.status;
  const statusLabel = rawStatus ? statusLabels[rawStatus] ?? rawStatus : "Unknown";

  const creationDate = offer.offerCreationDate
      ? new Date(offer.offerCreationDate)
      : null;

  const steamAccount = offer.steamAccount;
  const img = offer.item.catalog.imageLink ?? "";
  const rarity = offer.item?.details?.rarity ?? "";
  const rarityColor = RARITY_COLORS[rarity as RarityGrade] ?? "#ffffff";
  const itemName = offer.item?.catalog.marketHashName ?? "Unknown Item";

  const [infoOpen, setInfoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [lightModalOpen, setLightModalOpen] = useState(false);

  const floatPercent =
      floatValue !== null ? Math.min(Math.max(floatValue * 100, 0), 100) : 0;

  return (
      <>
        <TableRow className="hover:bg-neutral-800 transition-colors">
          {/* ITEM */}
          <TableCell padding="normal" sx={cellPaddingSX}>
            <div
                onClick={() => setLightModalOpen(true)}
                className="md:flex items-center gap-3 cursor-pointer group/item"
            >
              <div className="relative flex items-center shrink-0">
                {/* Left vertical rarity bar */}
                <div style={{ width: 4, height: 48, background: rarityColor }} />

                {/* Image item container with background fade effect */}
                <div className="relative ml-1">
                  {img ? (
                      <img
                          src={img}
                          alt={itemName}
                          className="w-12 h-12 object-contain z-10 relative drop-shadow-md transition-transform duration-200 group-hover/item:scale-105"
                      />
                  ) : (
                      <div className="w-12 h-12 bg-neutral-700 z-10 relative" />
                  )}
                  {/* Linear gradient glow effect */}
                  <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: -4,
                        transform: 'translateY(-50%)',
                        width: 52,
                        height: 48,
                        background: `linear-gradient(to right, ${rarityColor}22, transparent)`,
                        zIndex: 0
                      }}
                  />
                </div>
              </div>

              {quantity > 1 && (
                  <span className="sm:hidden text-[0.7rem] font-bold text-gray-300 mr-1">
                {quantity}x
              </span>
              )}

              <span className="text-white text-sm group-hover/item:text-dark-primary transition-colors">{itemName}</span>
            </div>
          </TableCell>

          {/* PRICE + STATUS MOBILE */}
          <TableCell padding="normal" sx={cellPaddingSX} className="text-white">
            <div className="flex flex-col">
              <span>{price !== null ? `${price.toFixed(2)} €` : "-"}</span>
              <span className="lg:hidden text-xs text-gray-400">
              {statusLabel}
            </span>
            </div>
          </TableCell>

          {/* QUANTITY */}
          <TableCell
              padding="normal"
              sx={{ display: { xs: "none", sm: "table-cell" }, ...cellPaddingSX }}
              className="text-white"
          >
            {quantity}
          </TableCell>

          {/* STEAM USER */}
          <TableCell
              padding="normal"
              sx={{ display: { xs: "none", md: "table-cell" }, ...cellPaddingSX }}
          >
            {steamAccount ? (
                <a
                    href={steamAccount.url ?? ""}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                >
                  {steamAccount.image ? (
                      <img
                          src={steamAccount.image}
                          alt={steamAccount.name ?? ""}
                          className="w-8 h-8 rounded-full"
                      />
                  ) : (
                      <div className="w-8 h-8 rounded-full bg-neutral-700" />
                  )}
                  <span>{steamAccount.name ?? "Unknown"}</span>
                </a>
            ) : (
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="w-8 h-8 rounded-full bg-neutral-700" />
                  <span>Unknown</span>
                </div>
            )}
          </TableCell>

          {/* STATUS */}
          <TableCell
              padding="normal"
              sx={{ display: { xs: "none", md: "table-cell" }, ...cellPaddingSX }}
              className="text-gray-300"
          >
            {statusLabel}
          </TableCell>

          {/* FLOAT */}
          <TableCell
              padding="normal"
              sx={{ display: { xs: "none", lg: "table-cell" }, ...cellPaddingSX }}
          >
            {floatValue === null ? (
                <span className="text-gray-500">-</span>
            ) : (
                <div className="flex flex-col gap-1" style={{ minWidth: 120 }}>
                  <div className="text-gray-300 text-xs">
                    {floatValue.toFixed(8)}
                  </div>

                  <div className="relative h-2 rounded overflow-hidden bg-gray-700">
                    <div
                        className="absolute top-0 h-full w-[2px] bg-white"
                        style={{
                          left: `${floatPercent}%`,
                          transform: "translateX(-1px)",
                        }}
                    />
                  </div>
                </div>
            )}
          </TableCell>

          {/* CREATED */}
          <TableCell
              padding="normal"
              sx={{ display: { xs: "none", xl: "table-cell" }, ...cellPaddingSX }}
              className="text-gray-300"
          >
            {creationDate ? creationDate.toLocaleDateString() : "-"}
          </TableCell>

          {/* ACTIONS */}
          <TableCell padding="normal" sx={cellPaddingSX}>
            <div className="flex md:gap-2">
              <IconButton color="primary" onClick={() => setInfoOpen(true)}>
                <Info />
              </IconButton>
              <IconButton color="warning" onClick={() => setEditOpen(true)}>
                <Edit />
              </IconButton>
              <IconButton color="error" onClick={() => setDeleteOpen(true)}>
                <Delete />
              </IconButton>
            </div>
          </TableCell>
        </TableRow>

        <ItemDetailLightModal
            open={lightModalOpen}
            onClose={() => setLightModalOpen(false)}
            item={offer.item}
        />

        <OfferInfoModal
            open={infoOpen}
            onClose={() => setInfoOpen(false)}
            offer={offer}
        />

        <OfferEditModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            offer={offer}
            onOffersDeleted={onOffersDeleted}
            onOffersUpdated={onOffersUpdated}
        />

        <OfferDeleteModal
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            offer={offer}
            onOffersDeleted={onOffersDeleted}
        />
      </>
  );
};

export default OnSaleRow;
